import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, Thumbnail, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../lib/utils";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Cancel01Icon,
  Search01Icon,
  RotateLeft01Icon,
  RotateRight01Icon,
  MinusSignCircleIcon,
  PlusSignCircleIcon,
  SidebarLeft01Icon,
  ArrowDown01Icon,
} from "@hugeicons/core-free-icons";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import * as PopoverPrimitive from "@radix-ui/react-popover";

// ── PDF.js worker setup ──
// Self-hosted to avoid CDN blockers in privacy browsers.
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

// ── Constants ──
const ZOOM_OPTIONS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 4, 8] as const;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 8;

// ── Search highlight helper ──
function highlightPattern(text: string, pattern: string, itemIndex: number) {
  if (!pattern) return text;
  return text.replace(
    new RegExp(pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"),
    (value: string) => `<mark id="search-result-${itemIndex}" style="background:var(--color-accent-400);color:white;border-radius:2px;padding:0 1px">${value}</mark>`,
  );
}

// ── Props ──
interface PdfViewerProps {
  url: string;
  title?: string;
  open: boolean;
  onClose: () => void;
}

export function PdfViewer({ url, title, open, onClose }: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSidebar, setShowSidebar] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const viewportRef = useRef<HTMLDivElement>(null);

  // ── Reset on open ──
  useEffect(() => {
    if (open) {
      setNumPages(null);
      setCurrentPage(1);
      setZoom(1);
      setRotation(0);
      setSearchQuery("");
      setShowSidebar(true);
      setIsLoading(true);
    }
  }, [open, url]);

  // ── Escape key ──
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener("keydown", handleKey, true);
    return () => window.removeEventListener("keydown", handleKey, true);
  }, [open, onClose]);

  // ── Document loaded ──
  const onDocumentLoadSuccess = useCallback(({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setIsLoading(false);
  }, []);

  // ── Text renderer for search highlighting ──
  const textRenderer = useCallback(
    (textItem: { str: string; itemIndex: number }) =>
      highlightPattern(textItem.str, searchQuery, textItem.itemIndex),
    [searchQuery],
  );

  // ── Intersection observer to track current page ──
  useEffect(() => {
    if (!viewportRef.current || !numPages) return;

    const options: IntersectionObserverInit = {
      root: viewportRef.current,
      rootMargin: "0px",
      threshold: 0.5,
    };

    const callback: IntersectionObserverCallback = (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const pageElement = entry.target.closest("[data-page-number]");
          if (pageElement) {
            const pageNumber = parseInt(
              pageElement.getAttribute("data-page-number") ?? "1",
              10,
            );
            setCurrentPage(pageNumber);
          }
        }
      }
    };

    const observer = new IntersectionObserver(callback, options);

    const mutationObserver = new MutationObserver(() => {
      const pages = viewportRef.current?.querySelectorAll(".react-pdf__Page");
      if (pages) {
        pages.forEach((page) => observer.observe(page));
      }
    });

    mutationObserver.observe(viewportRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [numPages]);

  // ── Zoom helpers ──
  const zoomIn = useCallback(() => {
    setZoom((z) => Math.min(MAX_ZOOM, +(z + 0.25).toFixed(2)));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((z) => Math.max(MIN_ZOOM, +(z - 0.25).toFixed(2)));
  }, []);

  // ── Scroll to page on thumbnail click ──
  const scrollToPage = useCallback((pageNum: number) => {
    if (!viewportRef.current) return;
    const el = viewportRef.current.querySelector(`[data-page-number="${pageNum}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className="fixed inset-0 z-[100] flex flex-col bg-[var(--color-bg-canvas)]"
        >
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            className="flex flex-row flex-1 min-h-0"
            loading={null}
          >
            {/* ── Loading state ── */}
            {isLoading && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-[var(--spacing-3)] bg-[var(--color-bg-canvas)]">
                <div className="w-[32px] h-[32px] rounded-full border-2 border-[var(--color-accent)] border-t-transparent animate-spin" />
                <span className="text-[13px] text-[var(--color-text-muted)]">Loading PDF…</span>
              </div>
            )}

            {/* ── Sidebar with thumbnails ── */}
            {showSidebar && numPages && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 200, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.18 }}
                className={cn(
                  "shrink-0 border-r border-[var(--color-border-stroke)]",
                  "bg-[var(--color-bg-sidebar)] flex flex-col overflow-hidden",
                )}
              >
                <ScrollAreaPrimitive.Root className="flex-1 overflow-hidden">
                  <ScrollAreaPrimitive.Viewport className="h-full w-full p-[var(--spacing-3)]">
                    <div className="flex flex-col items-center gap-[var(--spacing-2)]">
                      {Array.from({ length: numPages }, (_, i) => (
                        <button
                          key={`thumb_${i + 1}`}
                          onClick={() => scrollToPage(i + 1)}
                          className={cn(
                            "flex flex-col items-center gap-[var(--spacing-1)] p-[var(--spacing-1_5)]",
                            "rounded-[var(--radius-md)] w-full cursor-pointer",
                            "transition-colors duration-[120ms]",
                            "hover:bg-[var(--color-bg-hover)]",
                            currentPage === i + 1 && "bg-[var(--color-bg-hover)] ring-1 ring-[var(--color-accent)]/30",
                          )}
                        >
                          <Thumbnail
                            pageNumber={i + 1}
                            width={160}
                            rotate={rotation}
                            onItemClick={({ pageNumber: pg }) => scrollToPage(pg)}
                            className="rounded-[var(--radius-sm)] border border-[var(--color-border-stroke)] shadow-sm overflow-hidden"
                          />
                          <span className={cn(
                            "text-[11px] tabular-nums",
                            currentPage === i + 1
                              ? "text-[var(--color-accent)] font-medium"
                              : "text-[var(--color-text-muted)]",
                          )}>
                            {i + 1}
                          </span>
                        </button>
                      ))}
                    </div>
                  </ScrollAreaPrimitive.Viewport>
                  <ScrollAreaPrimitive.Scrollbar
                    orientation="vertical"
                    className="flex w-[6px] touch-none p-[1px] select-none transition-colors"
                  >
                    <ScrollAreaPrimitive.Thumb className="relative flex-1 rounded-full bg-[var(--color-border-strong)] opacity-40 hover:opacity-60" />
                  </ScrollAreaPrimitive.Scrollbar>
                </ScrollAreaPrimitive.Root>
              </motion.div>
            )}

            {/* ── Main area ── */}
            <div className="flex-1 flex flex-col min-w-0">
              {/* ── Toolbar ── */}
              <div className={cn(
                "shrink-0 flex items-center justify-between",
                "h-[44px] px-[var(--spacing-3)]",
                "border-b border-[var(--color-border-stroke)]",
                "bg-[var(--color-bg-surface)]",
              )}>
                {/* Left group */}
                <div className="flex items-center gap-[var(--spacing-2)]">
                  {/* Sidebar toggle */}
                  <button
                    onClick={() => setShowSidebar((s) => !s)}
                    className={cn(
                      "h-[28px] w-[28px] flex items-center justify-center",
                      "rounded-[var(--radius-sm)]",
                      "text-[var(--color-icon)] hover:text-[var(--color-text-primary)]",
                      "hover:bg-[var(--color-bg-hover)]",
                      "transition-colors duration-[120ms] cursor-pointer",
                      showSidebar && "bg-[var(--color-bg-hover)] text-[var(--color-text-primary)]",
                    )}
                    aria-label="Toggle sidebar"
                  >
                    <HugeiconsIcon icon={SidebarLeft01Icon} size={16} />
                  </button>

                  {/* Page indicator */}
                  <span className="text-[13px] text-[var(--color-text-muted)] tabular-nums select-none">
                    Page {currentPage} of {numPages ?? "…"}
                  </span>

                  {/* Title */}
                  {title && (
                    <>
                      <div className="w-[1px] h-[16px] bg-[var(--color-border-stroke)]" />
                      <span className="text-[13px] text-[var(--color-text-secondary)] truncate max-w-[240px]">
                        {title}
                      </span>
                    </>
                  )}
                </div>

                {/* Right group */}
                <div className="flex items-center gap-[var(--spacing-1)]">
                  {/* Rotate */}
                  <button
                    onClick={() => setRotation((r) => r - 90)}
                    className={cn(
                      "h-[28px] w-[28px] flex items-center justify-center",
                      "rounded-[var(--radius-sm)]",
                      "text-[var(--color-icon)] hover:text-[var(--color-text-primary)]",
                      "hover:bg-[var(--color-bg-hover)]",
                      "transition-colors duration-[120ms] cursor-pointer",
                    )}
                    aria-label="Rotate left"
                  >
                    <HugeiconsIcon icon={RotateLeft01Icon} size={16} />
                  </button>
                  <button
                    onClick={() => setRotation((r) => r + 90)}
                    className={cn(
                      "h-[28px] w-[28px] flex items-center justify-center",
                      "rounded-[var(--radius-sm)]",
                      "text-[var(--color-icon)] hover:text-[var(--color-text-primary)]",
                      "hover:bg-[var(--color-bg-hover)]",
                      "transition-colors duration-[120ms] cursor-pointer",
                    )}
                    aria-label="Rotate right"
                  >
                    <HugeiconsIcon icon={RotateRight01Icon} size={16} />
                  </button>

                  {/* Separator */}
                  <div className="w-[1px] h-[16px] bg-[var(--color-border-stroke)] mx-[var(--spacing-1)]" />

                  {/* Zoom controls */}
                  <button
                    onClick={zoomOut}
                    disabled={zoom <= MIN_ZOOM}
                    className={cn(
                      "h-[28px] w-[28px] flex items-center justify-center",
                      "rounded-[var(--radius-sm)]",
                      "text-[var(--color-icon)] hover:text-[var(--color-text-primary)]",
                      "hover:bg-[var(--color-bg-hover)]",
                      "transition-colors duration-[120ms] cursor-pointer",
                      "disabled:opacity-30 disabled:cursor-not-allowed",
                    )}
                    aria-label="Zoom out"
                  >
                    <HugeiconsIcon icon={MinusSignCircleIcon} size={16} />
                  </button>
                  <button
                    onClick={zoomIn}
                    disabled={zoom >= MAX_ZOOM}
                    className={cn(
                      "h-[28px] w-[28px] flex items-center justify-center",
                      "rounded-[var(--radius-sm)]",
                      "text-[var(--color-icon)] hover:text-[var(--color-text-primary)]",
                      "hover:bg-[var(--color-bg-hover)]",
                      "transition-colors duration-[120ms] cursor-pointer",
                      "disabled:opacity-30 disabled:cursor-not-allowed",
                    )}
                    aria-label="Zoom in"
                  >
                    <HugeiconsIcon icon={PlusSignCircleIcon} size={16} />
                  </button>

                  {/* Zoom select */}
                  <div className="relative">
                    <select
                      value={zoom.toString()}
                      onChange={(e) => setZoom(Number(e.target.value))}
                      className={cn(
                        "h-[28px] w-[72px] rounded-[var(--radius-sm)]",
                        "border border-[var(--color-border-stroke)]",
                        "bg-[var(--color-bg-canvas)] pl-[var(--spacing-2)] pr-[var(--spacing-6)]",
                        "text-[12px] tabular-nums text-[var(--color-text-primary)]",
                        "appearance-none cursor-pointer",
                        "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
                        "transition-colors duration-[120ms]",
                      )}
                    >
                      {ZOOM_OPTIONS.map((opt) => (
                        <option key={opt} value={opt.toString()}>
                          {`${opt * 100}%`}
                        </option>
                      ))}
                    </select>
                    <HugeiconsIcon
                      icon={ArrowDown01Icon}
                      size={12}
                      className="absolute right-[var(--spacing-1_5)] top-1/2 -translate-y-1/2 pointer-events-none text-[var(--color-icon)]"
                    />
                  </div>

                  {/* Separator */}
                  <div className="w-[1px] h-[16px] bg-[var(--color-border-stroke)] mx-[var(--spacing-1)]" />

                  {/* Search popover */}
                  <PopoverPrimitive.Root>
                    <PopoverPrimitive.Trigger asChild>
                      <button
                        className={cn(
                          "h-[28px] w-[28px] flex items-center justify-center",
                          "rounded-[var(--radius-sm)]",
                          "text-[var(--color-icon)] hover:text-[var(--color-text-primary)]",
                          "hover:bg-[var(--color-bg-hover)]",
                          "transition-colors duration-[120ms] cursor-pointer",
                          searchQuery && "bg-[var(--color-accent-900)] text-[var(--color-accent)]",
                        )}
                        aria-label="Search in document"
                      >
                        <HugeiconsIcon icon={Search01Icon} size={16} />
                      </button>
                    </PopoverPrimitive.Trigger>
                    <PopoverPrimitive.Portal>
                      <PopoverPrimitive.Content
                        align="end"
                        sideOffset={8}
                        className={cn(
                          "w-[260px] rounded-[var(--radius-md)]",
                          "border border-[var(--color-border-stroke)]",
                          "bg-[var(--color-bg-sidebar)] shadow-lg p-[var(--spacing-2)]",
                          "data-[state=open]:animate-fade-in",
                          "focus-visible:outline-none",
                        )}
                      >
                        <div className={cn(
                          "flex items-center gap-[var(--spacing-2)] h-[32px]",
                          "rounded-[var(--radius-sm)] border border-[var(--color-border-stroke)]",
                          "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)]",
                          "focus-within:border-[var(--color-accent-focus)]",
                          "transition-colors duration-[120ms]",
                        )}>
                          <HugeiconsIcon
                            icon={Search01Icon}
                            size={14}
                            className="shrink-0 text-[var(--color-icon)] opacity-60"
                          />
                          <input
                            type="text"
                            placeholder="Search in PDF…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            autoFocus
                            className={cn(
                              "flex-1 h-full min-w-0 bg-transparent border-none outline-none shadow-none ring-0",
                              "text-[13px] text-[var(--color-text-primary)]",
                              "placeholder:text-[var(--color-text-muted)]",
                            )}
                          />
                          {searchQuery && (
                            <button
                              onClick={() => setSearchQuery("")}
                              className="p-[2px] text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors cursor-pointer"
                              aria-label="Clear search"
                            >
                              <HugeiconsIcon icon={Cancel01Icon} size={12} />
                            </button>
                          )}
                        </div>
                      </PopoverPrimitive.Content>
                    </PopoverPrimitive.Portal>
                  </PopoverPrimitive.Root>

                  {/* Separator */}
                  <div className="w-[1px] h-[16px] bg-[var(--color-border-stroke)] mx-[var(--spacing-1)]" />

                  {/* Close */}
                  <button
                    onClick={onClose}
                    className={cn(
                      "h-[28px] w-[28px] flex items-center justify-center",
                      "rounded-[var(--radius-sm)]",
                      "text-[var(--color-icon)] hover:text-[var(--color-text-primary)]",
                      "hover:bg-[var(--color-bg-hover)]",
                      "transition-colors duration-[120ms] cursor-pointer",
                    )}
                    aria-label="Close viewer"
                  >
                    <HugeiconsIcon icon={Cancel01Icon} size={16} />
                  </button>
                </div>
              </div>

              {/* ── PDF pages viewport ── */}
              <ScrollAreaPrimitive.Root className="flex-1 overflow-hidden">
                <ScrollAreaPrimitive.Viewport
                  ref={viewportRef}
                  className="h-full w-full"
                >
                  <div className="flex flex-col items-center p-[var(--spacing-6)] gap-[var(--spacing-6)]">
                    {numPages && Array.from({ length: numPages }, (_, i) => (
                      <Page
                        key={`page_${i + 1}`}
                        pageNumber={i + 1}
                        data-page-number={i + 1}
                        scale={zoom}
                        rotate={rotation}
                        renderAnnotationLayer={false}
                        loading={null}
                        customTextRenderer={textRenderer}
                        className={cn(
                          "shadow-md rounded-[2px]",
                          "border border-[var(--color-border-stroke)]",
                        )}
                      />
                    ))}
                  </div>
                </ScrollAreaPrimitive.Viewport>
                <ScrollAreaPrimitive.Scrollbar
                  orientation="vertical"
                  className="flex w-[8px] touch-none p-[1px] select-none transition-colors"
                >
                  <ScrollAreaPrimitive.Thumb className="relative flex-1 rounded-full bg-[var(--color-border-strong)] opacity-40 hover:opacity-60" />
                </ScrollAreaPrimitive.Scrollbar>
                <ScrollAreaPrimitive.Scrollbar
                  orientation="horizontal"
                  className="flex h-[8px] touch-none p-[1px] select-none transition-colors flex-col"
                >
                  <ScrollAreaPrimitive.Thumb className="relative flex-1 rounded-full bg-[var(--color-border-strong)] opacity-40 hover:opacity-60" />
                </ScrollAreaPrimitive.Scrollbar>
              </ScrollAreaPrimitive.Root>
            </div>
          </Document>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
